import {
  createContext,
  createElement,
  useContext,
  useState,
  useSyncExternalStore,
  type PropsWithChildren,
} from "react";
import type { ProjectFileMessageReference } from "@papersflow/contracts/project-files";
import type { ReviewComment } from "@papersflow/contracts/review-comments";
import {
  inferComposerContextFileMediaType,
  isProtectedComposerContextPath,
  PROTECTED_COMPOSER_CONTEXT_PATH_MESSAGE,
} from "./composerContextFiles";

export interface ComposerContextTarget {
  readonly workspaceId: string;
  readonly projectId: string;
  readonly threadId: string;
  readonly root: string;
}

export type ComposerProjectFileTarget = Pick<
  ComposerContextTarget,
  "workspaceId" | "projectId" | "threadId"
>;

export interface EnqueueComposerTextContextInput {
  readonly target: ComposerProjectFileTarget;
  readonly name: string;
  readonly text: string;
}

export interface LocalComposerContextAuthority extends ComposerContextTarget {
  readonly grantId: string;
  readonly canWriteThreads: boolean;
}

export type ComposerContextIngressStageOutcome =
  | { readonly status: "ready"; readonly relativePath: string }
  | { readonly status: "rejected"; readonly relativePath: string; readonly message: string }
  | { readonly status: "stale"; readonly relativePath: string }
  | { readonly status: "read-failed"; readonly relativePath: string };

export type ComposerContextIngressOutcome =
  | ComposerContextIngressStageOutcome
  | {
      readonly status: "unavailable";
      readonly reason: "no-local-authority" | "no-writable-composer" | "read-only";
    };

export type ComposerProjectFileIngressStageOutcome =
  | {
      readonly status: "ready";
      readonly reference: ProjectFileMessageReference;
      readonly alreadyStaged: boolean;
    }
  | {
      readonly status: "rejected";
      readonly reference: ProjectFileMessageReference;
      readonly message: string;
    }
  | { readonly status: "stale"; readonly reference: ProjectFileMessageReference };

export type ComposerProjectFileIngressOutcome =
  | ComposerProjectFileIngressStageOutcome
  | Extract<ComposerContextIngressOutcome, { status: "unavailable" }>;

export type ComposerReviewCommentIngressStageOutcome =
  | {
      readonly status: "ready";
      readonly comment: ReviewComment;
      readonly alreadyStaged: boolean;
    }
  | {
      readonly status: "rejected";
      readonly comment: ReviewComment;
      readonly message: string;
    }
  | { readonly status: "stale"; readonly comment: ReviewComment };

type ComposerRegistrationUnavailableOutcome = {
  readonly status: "unavailable";
  readonly reason: "no-writable-composer";
};

export type ComposerReviewCommentIngressOutcome =
  | ComposerReviewCommentIngressStageOutcome
  | ComposerRegistrationUnavailableOutcome;

export type LocalTextFileContextSource =
  | { readonly kind: "read"; readonly read: () => Promise<string> }
  | { readonly kind: "loaded"; readonly text: string };

export interface EnqueueLocalTextFileContextInput {
  readonly target: ComposerContextTarget;
  readonly relativePath: string;
  readonly source: LocalTextFileContextSource;
}

export interface EnqueueActiveLocalTextFileContextInput {
  readonly workspaceId: string;
  readonly projectId: string;
  readonly threadId: string;
  readonly relativePath: string;
  readonly read: (grantId: string, relativePath: string) => Promise<string>;
}

export interface ComposerPathSearchEntry {
  readonly name: string;
  readonly relativePath: string;
}

export type ComposerPathSearchOutcome =
  | {
      readonly status: "ready";
      readonly entries: readonly ComposerPathSearchEntry[];
      readonly truncated: boolean;
    }
  | { readonly status: "rejected"; readonly message: string }
  | { readonly status: "stale" }
  | { readonly status: "read-failed" }
  | Extract<ComposerContextIngressOutcome, { status: "unavailable" }>;

export interface SearchActiveLocalPathsInput {
  readonly target: ComposerProjectFileTarget;
  readonly query: string;
  readonly list: (
    grantId: string,
    relativePath: string,
  ) => Promise<
    readonly {
      readonly name: string;
      readonly relativePath: string;
      readonly isDirectory: boolean;
    }[]
  >;
}

export interface ComposerContextRegistration {
  readonly workspaceId: string;
  readonly projectId: string;
  readonly threadId: string;
  readonly canWriteThreads: boolean;
  readonly stageFile: (
    file: File,
    isLeaseCurrent: () => boolean,
  ) => Promise<ComposerContextIngressStageOutcome>;
  readonly stageProjectFile: (
    reference: ProjectFileMessageReference,
    isLeaseCurrent: () => boolean,
  ) => ComposerProjectFileIngressStageOutcome;
  readonly stageReviewComment: (
    comment: ReviewComment,
    isLeaseCurrent: () => boolean,
  ) => ComposerReviewCommentIngressStageOutcome;
}

type UnavailableOutcome = Extract<ComposerContextIngressOutcome, { status: "unavailable" }>;
type Availability = { readonly available: true } | UnavailableOutcome;

interface RegisteredComposer extends ComposerContextRegistration {
  readonly token: object;
}

interface PublishedAuthority extends LocalComposerContextAuthority {
  readonly token: object;
}

interface ComposerLease {
  readonly generation: number;
  readonly composer: RegisteredComposer;
  readonly authority: PublishedAuthority;
}

interface ComposerRegistrationLease {
  readonly generation: number;
  readonly composer: RegisteredComposer;
}

const MAX_PATH_SEARCH_QUERY_CHARACTERS = 120;
const MAX_PATH_SEARCH_DIRECTORIES = 80;
const MAX_PATH_SEARCH_SCANNED_ENTRIES = 5_000;
const MAX_PATH_SEARCH_RESULTS = 40;
const SKIPPED_PATH_SEARCH_DIRECTORIES = new Set([
  ".git",
  ".next",
  ".turbo",
  "build",
  "coverage",
  "dist",
  "node_modules",
  "target",
  "vendor",
]);

function pathSearchRank(name: string, relativePath: string, query: string): number {
  const normalizedName = name.toLowerCase();
  const normalizedPath = relativePath.toLowerCase();
  if (normalizedName === query) return 0;
  if (normalizedName.startsWith(query)) return 1;
  if (normalizedPath.startsWith(query)) return 2;
  return normalizedName.includes(query) ? 3 : 4;
}

type LocalPathSearchResult = Extract<
  ComposerPathSearchOutcome,
  { readonly status: "ready" | "stale" | "read-failed" }
>;

// oxlint-disable-next-line eslint/complexity -- The bounded traversal handles directory, file, protection, ranking, and stale-lease outcomes in one pass.
async function searchLocalPaths(input: {
  readonly grantId: string;
  readonly query: string;
  readonly list: SearchActiveLocalPathsInput["list"];
  readonly isCurrent: () => boolean;
}): Promise<LocalPathSearchResult> {
  const directories = [""];
  const queuedDirectories = new Set(directories);
  const candidates: ComposerPathSearchEntry[] = [];
  const candidatePaths = new Set<string>();
  let directoryIndex = 0;
  let scannedEntries = 0;
  let truncated = false;
  while (
    directoryIndex < directories.length &&
    directoryIndex < MAX_PATH_SEARCH_DIRECTORIES &&
    scannedEntries < MAX_PATH_SEARCH_SCANNED_ENTRIES
  ) {
    const directory = directories[directoryIndex] ?? "";
    directoryIndex += 1;
    let entries;
    try {
      // oxlint-disable-next-line eslint/no-await-in-loop -- Sequential reads enforce the directory and entry budgets without a native-call fanout.
      entries = await input.list(input.grantId, directory);
    } catch {
      if (!input.isCurrent()) return { status: "stale" };
      if (directory === "") return { status: "read-failed" };
      truncated = true;
      continue;
    }
    if (!input.isCurrent()) return { status: "stale" };
    for (const entry of entries) {
      if (scannedEntries >= MAX_PATH_SEARCH_SCANNED_ENTRIES) {
        truncated = true;
        break;
      }
      scannedEntries += 1;
      const relativePath = canonicalComposerContextRelativePath(entry.relativePath);
      if (relativePath === null || isProtectedComposerContextPath(relativePath)) continue;
      const name = relativePath.slice(relativePath.lastIndexOf("/") + 1);
      if (entry.isDirectory) {
        if (
          !SKIPPED_PATH_SEARCH_DIRECTORIES.has(name.toLowerCase()) &&
          !queuedDirectories.has(relativePath)
        ) {
          queuedDirectories.add(relativePath);
          directories.push(relativePath);
        }
        continue;
      }
      if (
        inferComposerContextFileMediaType({ name, type: "" }) === null ||
        candidatePaths.has(relativePath)
      ) {
        continue;
      }
      if (relativePath.toLowerCase().includes(input.query)) {
        candidatePaths.add(relativePath);
        candidates.push({ name, relativePath });
      }
    }
  }
  if (directoryIndex < directories.length) truncated = true;
  candidates.sort((left, right) => {
    const leftRank = pathSearchRank(left.name, left.relativePath, input.query);
    const rightRank = pathSearchRank(right.name, right.relativePath, input.query);
    if (leftRank !== rightRank) return leftRank - rightRank;
    if (left.relativePath.length !== right.relativePath.length) {
      return left.relativePath.length - right.relativePath.length;
    }
    return left.relativePath < right.relativePath
      ? -1
      : left.relativePath > right.relativePath
        ? 1
        : 0;
  });
  if (candidates.length > MAX_PATH_SEARCH_RESULTS) truncated = true;
  return {
    status: "ready",
    entries: candidates.slice(0, MAX_PATH_SEARCH_RESULTS),
    truncated,
  };
}

function leaseAvailability(
  lease: ComposerLease | ComposerRegistrationLease | UnavailableOutcome,
): Availability {
  return "generation" in lease ? { available: true } : lease;
}

function subscribeComposerIngressListener(
  listeners: Set<() => void>,
  listener: () => void,
): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function sameScope(
  left: Pick<ComposerContextTarget, "workspaceId" | "projectId" | "threadId">,
  right: Pick<ComposerContextTarget, "workspaceId" | "projectId" | "threadId">,
): boolean {
  return (
    left.workspaceId === right.workspaceId &&
    left.projectId === right.projectId &&
    left.threadId === right.threadId
  );
}

export function canonicalComposerContextRelativePath(value: string): string | null {
  if (
    value.length === 0 ||
    value.startsWith("/") ||
    value.startsWith("\\") ||
    value.includes("\\") ||
    /^[A-Za-z][A-Za-z\d+.-]*:/.test(value) ||
    Array.from(value).some((character) => {
      const codePoint = character.codePointAt(0) ?? 0;
      return codePoint <= 0x1f || (codePoint >= 0x7f && codePoint <= 0x9f);
    })
  ) {
    return null;
  }
  const segments = value.split("/");
  return segments.some((segment) => segment.length === 0 || segment === "." || segment === "..")
    ? null
    : value;
}

function protectedComposerContextPathOutcome(
  relativePath: string,
): ComposerContextIngressStageOutcome {
  return {
    status: "rejected",
    relativePath,
    message: PROTECTED_COMPOSER_CONTEXT_PATH_MESSAGE,
  };
}

function validatedLocalComposerContextPath(
  value: string,
): string | ComposerContextIngressStageOutcome {
  const relativePath = canonicalComposerContextRelativePath(value);
  if (relativePath === null) {
    return {
      status: "rejected",
      relativePath: value,
      message: "Only a canonical relative file path can be added.",
    };
  }
  return isProtectedComposerContextPath(relativePath)
    ? protectedComposerContextPathOutcome(relativePath)
    : relativePath;
}

function validatedComposerTextContextName(
  value: string,
): string | ComposerContextIngressStageOutcome {
  const name = canonicalComposerContextRelativePath(value);
  if (name === null || name.includes("/")) {
    return {
      status: "rejected",
      relativePath: value,
      message: "Text context needs a canonical file name.",
    };
  }
  return isProtectedComposerContextPath(name) ? protectedComposerContextPathOutcome(name) : name;
}

function enqueueAuthorizedLocalTextFile(
  authority: PublishedAuthority | null,
  input: EnqueueActiveLocalTextFileContextInput,
  enqueue: (input: EnqueueLocalTextFileContextInput) => Promise<ComposerContextIngressOutcome>,
): Promise<ComposerContextIngressOutcome> {
  if (!authority || !sameScope(authority, input)) {
    return Promise.resolve({ status: "unavailable", reason: "no-local-authority" });
  }
  return enqueue({
    target: {
      workspaceId: authority.workspaceId,
      projectId: authority.projectId,
      threadId: authority.threadId,
      root: authority.root,
    },
    relativePath: input.relativePath,
    source: {
      kind: "read",
      read: () => input.read(authority.grantId, input.relativePath),
    },
  });
}

// oxlint-disable-next-line eslint/max-lines-per-function -- One closure must own the mutable composer/authority generation so every ingress operation shares the same revocable lease.
export function createComposerContextIngressController() {
  let generation = 0;
  let composer: RegisteredComposer | null = null;
  let authority: PublishedAuthority | null = null;
  const listeners = new Set<() => void>();

  function changed() {
    generation += 1;
    for (const listener of listeners) listener();
  }

  function registerComposer(registration: ComposerContextRegistration): () => void {
    if (!registration.canWriteThreads) return () => undefined;
    const token = {};
    composer = { ...registration, token };
    changed();
    return () => {
      if (composer?.token !== token) return;
      composer = null;
      changed();
    };
  }

  function publishLocalAuthority(nextAuthority: LocalComposerContextAuthority): () => void {
    const token = {};
    authority = { ...nextAuthority, token };
    changed();
    return () => {
      if (authority?.token !== token) return;
      authority = null;
      changed();
    };
  }

  function acquireLease(target: ComposerContextTarget): ComposerLease | UnavailableOutcome {
    const currentAuthority = authority;
    const currentComposer = composer;
    if (
      !currentAuthority ||
      !sameScope(currentAuthority, target) ||
      currentAuthority.root !== target.root
    ) {
      return { status: "unavailable", reason: "no-local-authority" };
    }
    if (!currentAuthority.canWriteThreads) {
      return { status: "unavailable", reason: "read-only" };
    }
    if (!currentComposer || !sameScope(currentComposer, target)) {
      return { status: "unavailable", reason: "no-writable-composer" };
    }
    return { generation, composer: currentComposer, authority: currentAuthority };
  }

  function acquireComposerLease(
    target: ComposerProjectFileTarget,
  ): ComposerRegistrationLease | ComposerRegistrationUnavailableOutcome {
    const currentComposer = composer;
    if (!currentComposer || !sameScope(currentComposer, target)) {
      return { status: "unavailable", reason: "no-writable-composer" };
    }
    return { generation, composer: currentComposer };
  }

  function acquireActiveLocalLease(
    target: ComposerProjectFileTarget,
  ): ComposerLease | UnavailableOutcome {
    const currentAuthority = authority;
    if (!currentAuthority || !sameScope(currentAuthority, target)) {
      return { status: "unavailable", reason: "no-local-authority" };
    }
    return acquireLease({ ...target, root: currentAuthority.root });
  }

  function getAvailability(target: ComposerContextTarget): Availability {
    return leaseAvailability(acquireLease(target));
  }

  function isLeaseCurrent(lease: ComposerLease): boolean {
    return (
      generation === lease.generation &&
      composer?.token === lease.composer.token &&
      authority?.token === lease.authority.token
    );
  }

  function isComposerLeaseCurrent(lease: ComposerRegistrationLease): boolean {
    return generation === lease.generation && composer?.token === lease.composer.token;
  }

  function getProjectFileAvailability(target: ComposerProjectFileTarget): Availability {
    return leaseAvailability(acquireComposerLease(target));
  }

  function getReviewCommentAvailability(
    target: ComposerProjectFileTarget,
  ): { readonly available: true } | ComposerRegistrationUnavailableOutcome {
    const lease = acquireComposerLease(target);
    return "generation" in lease ? { available: true } : lease;
  }

  function enqueueProjectFile(input: {
    readonly target: ComposerProjectFileTarget;
    readonly reference: ProjectFileMessageReference;
  }): ComposerProjectFileIngressOutcome {
    const lease = acquireComposerLease(input.target);
    if (!("generation" in lease)) return lease;
    if (!isComposerLeaseCurrent(lease)) {
      return { status: "stale", reference: input.reference };
    }
    return lease.composer.stageProjectFile(input.reference, () => isComposerLeaseCurrent(lease));
  }

  function enqueueReviewComment(input: {
    readonly target: ComposerProjectFileTarget;
    readonly comment: ReviewComment;
  }): ComposerReviewCommentIngressOutcome {
    const lease = acquireComposerLease(input.target);
    if (!("generation" in lease)) return lease;
    if (!isComposerLeaseCurrent(lease)) {
      return { status: "stale", comment: input.comment };
    }
    return lease.composer.stageReviewComment(input.comment, () => isComposerLeaseCurrent(lease));
  }

  function enqueueTextContext(
    input: EnqueueComposerTextContextInput,
  ): Promise<ComposerContextIngressOutcome> {
    const lease = acquireComposerLease(input.target);
    if (!("generation" in lease)) return Promise.resolve(lease);
    const name = validatedComposerTextContextName(input.name);
    if (typeof name !== "string") return Promise.resolve(name);
    if (input.text.length === 0) {
      return Promise.resolve({
        status: "rejected",
        relativePath: name,
        message: "Select non-empty artifact content first.",
      });
    }
    const file = new File([input.text], name, { type: "text/plain" });
    return lease.composer.stageFile(file, () => isComposerLeaseCurrent(lease));
  }

  async function searchActiveLocalPaths(
    input: SearchActiveLocalPathsInput,
  ): Promise<ComposerPathSearchOutcome> {
    const lease = acquireActiveLocalLease(input.target);
    if (!("generation" in lease)) return lease;
    const query = input.query.trim().toLowerCase();
    if (query.length === 0 || query.length > MAX_PATH_SEARCH_QUERY_CHARACTERS) {
      return {
        status: "rejected",
        message: `Search with 1 to ${MAX_PATH_SEARCH_QUERY_CHARACTERS} characters.`,
      };
    }

    return searchLocalPaths({
      grantId: lease.authority.grantId,
      query,
      list: input.list,
      isCurrent: () => isLeaseCurrent(lease),
    });
  }

  function enqueueLocalTextFile(
    input: EnqueueLocalTextFileContextInput,
  ): Promise<ComposerContextIngressOutcome> {
    const lease = acquireLease(input.target);
    if (!("generation" in lease)) return Promise.resolve(lease);
    const relativePath = validatedLocalComposerContextPath(input.relativePath);
    if (typeof relativePath !== "string") return Promise.resolve(relativePath);

    const read =
      input.source.kind === "loaded" ? Promise.resolve(input.source.text) : input.source.read();
    return read.then(
      (text) => {
        if (!isLeaseCurrent(lease)) return { status: "stale", relativePath } as const;
        const file = new File([text], relativePath);
        if (!isLeaseCurrent(lease)) return { status: "stale", relativePath } as const;
        return lease.composer.stageFile(file, () => isLeaseCurrent(lease));
      },
      () =>
        isLeaseCurrent(lease)
          ? ({ status: "read-failed", relativePath } as const)
          : ({ status: "stale", relativePath } as const),
    );
  }

  return {
    enqueueActiveLocalTextFile: (input: EnqueueActiveLocalTextFileContextInput) =>
      enqueueAuthorizedLocalTextFile(authority, input, enqueueLocalTextFile),
    enqueueLocalTextFile,
    enqueueTextContext,
    enqueueProjectFile,
    enqueueReviewComment,
    searchActiveLocalPaths,
    getAvailability,
    getProjectFileAvailability,
    getReviewCommentAvailability,
    getRevision: () => generation,
    publishLocalAuthority,
    registerComposer,
    subscribe: (listener: () => void) => subscribeComposerIngressListener(listeners, listener),
  };
}

export type ComposerContextIngressController = ReturnType<
  typeof createComposerContextIngressController
>;

const ComposerContextIngressContext = createContext<ComposerContextIngressController | null>(null);

export function ComposerContextIngressProvider({ children }: PropsWithChildren) {
  const [controller] = useState(createComposerContextIngressController);
  return createElement(ComposerContextIngressContext.Provider, { value: controller }, children);
}

export function useComposerContextIngress(): ComposerContextIngressController {
  const controller = useContext(ComposerContextIngressContext);
  if (!controller) {
    throw new Error("useComposerContextIngress must be used inside ComposerContextIngressProvider");
  }
  useSyncExternalStore(
    (listener) => controller.subscribe(listener),
    () => controller.getRevision(),
    () => controller.getRevision(),
  );
  return controller;
}
